// Centralized session storage + access-token freshness.
//
// supabase-js's in-memory auth state is unreliable in this app — sign-in goes
// through our own /auth/v1/token call (not supabase.auth.signInWithPassword),
// and getSession() can wedge after tab-visibility changes. So we own the
// storage key and the refresh lifecycle directly. supabase-js is configured
// with autoRefreshToken: false (see ./supabase.ts) so we are the sole writer.

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/+$/, '');
const PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? '';
const PROJECT_REF = SUPABASE_URL.includes('://')
  ? new URL(SUPABASE_URL).hostname.split('.')[0]
  : '';
export const SESSION_STORAGE_KEY = `sb-${PROJECT_REF}-auth-token`;

export interface StoredSession {
  access_token: string;
  refresh_token: string;
  expires_at?: number;
  expires_in?: number;
  token_type?: string;
  user?: { id: string };
}

export function readSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as StoredSession;
    return session?.access_token ? session : null;
  } catch {
    return null;
  }
}

export function writeSession(session: StoredSession): void {
  try { localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session)); } catch { /* ignore */ }
}

export function clearSession(): void {
  try { localStorage.removeItem(SESSION_STORAGE_KEY); } catch { /* ignore */ }
}

// Refresh slightly before expiry so the access_token is still valid by the
// time the resulting request reaches Supabase / the backend.
const REFRESH_SKEW_SECONDS = 30;

let inFlight: Promise<string | null> | null = null;

/**
 * Returns a valid access token, refreshing via refresh_token if the stored
 * one has expired (or is within REFRESH_SKEW_SECONDS of expiry). Returns null
 * if there is no session, or the refresh failed (refresh_token revoked/expired) —
 * in that case the session is cleared from storage so the next bootstrap
 * routes the user back to LoginView.
 */
export async function getValidAccessToken(): Promise<string | null> {
  const session = readSession();
  if (!session) return null;
  const now = Math.floor(Date.now() / 1000);
  if (session.expires_at && session.expires_at - REFRESH_SKEW_SECONDS > now) {
    return session.access_token;
  }
  return refresh(session.refresh_token);
}

/**
 * Force a refresh now, regardless of expires_at. Used by the 401 retry path
 * in api/client.ts when the backend rejects an apparently-fresh token (e.g.,
 * clock skew, or a token rotated by another tab between our read and the
 * request landing).
 */
export async function forceRefresh(): Promise<string | null> {
  const session = readSession();
  if (!session) return null;
  return refresh(session.refresh_token);
}

function refresh(refreshToken: string): Promise<string | null> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
        method: 'POST',
        headers: { apikey: PUBLISHABLE_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) {
        clearSession();
        return null;
      }
      const next = await res.json() as StoredSession;
      if (!next?.access_token) {
        clearSession();
        return null;
      }
      writeSession(next);
      return next.access_token;
    } catch {
      // Network failure: leave storage intact so a subsequent attempt can retry.
      return null;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}
